// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — every colour that carries meaning, in one place so tests/contrast.test.ts can hold it
// to WCAG 2.1 §1.4.11 (≥3:1 for a non-text graphic) against every surface it can actually sit on.
//
// THE GOVERNING SPLIT, and the reason a burnt square can never read as an empty one: the board is
// PALE and everything that matters on it is DARK. A burnt square is the darkest thing on the board
// by a mile (10:1 against the pale squares), so the hole the arrow makes is the single most legible
// mark in the game — which is right, because the whole game is about where the holes are.
//
// The one place that split cannot be honoured is a dark token beside a dark burn: both are dark, so
// fill alone will not tell them apart. That is carried by the token's PALE RING, and the contrast
// test asserts exactly that rule — for every (mark, surface) pair, the fill OR the ring clears 3:1.
// Stating the mechanism in the test is the point; "it looks fine" is what shipped a 1.14:1 wall.

export const PALETTE = {
  night: '#131A17',
  panel: '#1D2622',
  panelHi: '#28332D',

  /** The two checker squares. Deliberately close together — the checker is orientation, not data. */
  glade: '#D9DFD3',
  shade: '#C2CBBC',
  /** The rim around the board, and the grid line between squares. */
  edge: '#6D7A70',

  /** Burnt ground. The darkest thing on the board, on purpose. */
  ash: '#241C1A',
  ember: '#C2410C',

  p1: '#A8330A',
  p2: '#123C74',
  /** The pale ring every token carries — what separates a dark token from dark ground. */
  ring: '#F4F1E6',

  /**
   * The huntress you have PICKED UP. Dark, not gold — and that is the whole lesson of this file.
   * The first cut ringed her in `gold` (#E9B44C), a light amber on a pale board: 1.45:1 on glade and
   * 1.24:1 on shade. The single most important feedback in a three-tap turn — "which one did I just
   * select?" — was effectively invisible, and the contrast gate could not see it because `gold` was
   * never in ON_BOARD. It is a board mark now, so it is held to the board's rule: pale board, dark
   * marks. 5.93:1 on glade, 4.82:1 on shade, 39 ΔE clear of its nearest neighbour.
   */
  pick: '#6B4A05',

  /** Stage one of a turn: where she may go. */
  step: '#1E5B2E',
  /**
   * Stage two: where the arrow may land. Plum rather than the obvious ember-red, because a red
   * arrow marker and the Rust player collapse to ΔE 10.9 under protanopia — a colour-blind player
   * reading "legal arrow square" as "an enemy huntress" is not a subtle failure.
   */
  shot: '#5B2168',
  /** Last turn's from/to squares, on open ground. */
  trail: '#2A2622',
  /**
   * ...and on burnt ground. She can shoot the doorway she just left, so the trail marker DOES land
   * on a charred square, where a dark dash is 1.11:1 and simply is not there.
   */
  trailBurnt: '#F4F1E6',

  text: '#EDF2EC',
  textMuted: '#AFBDB2',
  gold: '#E9B44C',
  alarm: '#FF9B76',
  /** HUD-only seat tints: the board colours are too dark to read on the night panel. */
  p1Hud: '#F0965A',
  p2Hud: '#7FC0EA',
} as const;

/** Every surface a meaningful mark is actually DRAWN ON. */
export const SURFACES = ['glade', 'shade', 'ash'] as const;

/**
 * Which surfaces each mark can ACTUALLY sit on. A blanket cross-product would be a lie in both
 * directions: a huntress is never on burnt ground (so holding her to it would force a worse
 * palette), and the trail marker genuinely is (so leaving it out would have shipped an invisible
 * one). Working out this map is most of the value of the gate.
 */
export const MARK_SURFACES: Record<string, ReadonlyArray<string>> = {
  p1: ['glade', 'shade'],
  p2: ['glade', 'shade'],
  pick: ['glade', 'shade'],
  step: ['glade', 'shade'],
  shot: ['glade', 'shade'],
  trail: ['glade', 'shade'],
  trailBurnt: ['ash'],
  ash: ['glade', 'shade'],
};

/** Everything that has to be findable, in the order the test reports them. */
export const ON_BOARD = ['p1', 'p2', 'pick', 'step', 'shot', 'trail', 'trailBurnt', 'ash'] as const;

/** A token's fill and the ring that rescues it where the fill cannot carry the difference. */
export const TOKEN_FILL = ['p1', 'p2'] as const;

export const SEAT_COLOUR = [PALETTE.p1, PALETTE.p2];
export const SEAT_HUD = [PALETTE.p1Hud, PALETTE.p2Hud];
export const SEAT_NAME = ['Rust', 'Slate'];
/** Silhouette, not just hue — the channel that survives any colour vision and any screenshot. */
export const SEAT_SHAPE = ['circle', 'diamond'];
